import FadeInSection from '$components/FadeInSection/FadeInSection';
import { SplitTextMulti } from '$components/SplitTextMulti/SplitTextMulti';
import { CtaProvider } from '$providers/CtaProvider';
import { TextRichText } from '$providers/Text';
import cn from 'classnames';
import { HTMLAttributes, Suspense } from 'react';

import {
  ContentBlockLayout,
  ContentBlockSize,
  ContentBlockType,
  IContentBlock,
} from './';

const layoutClasses: Record<ContentBlockLayout, string> = {
  center: 'text-center items-center justify-center',
  left: '',
};

const typeClasses: Record<ContentBlockType, string> = {
  light: 'text-grey-100',
  dark: 'text-white',
};
const typeClassesContent: Record<ContentBlockType, string> = {
  light: 'text-grey-80',
  dark: 'text-white',
};

const sizeClasses: Record<ContentBlockSize, string> = {
  xLarge: 'style-heading-m',
  large: 'style-heading-s',
  medium: 'style-heading-xs',
  small: 'style-heading-xxs',
  gallery: 'style-heading-xxxs',
};

const sizeSubClasses: Record<ContentBlockSize, string> = {
  xLarge: 'style-heading-xxs',
  large: 'style-heading-xxs',
  medium: 'style-heading-xxxs',
  small: 'style-heading-xxxs',
  gallery: 'style-heading-xxxs',
};

const sizeBodyClasses: Record<ContentBlockSize, string> = {
  xLarge: 'style-body-l',
  large: 'style-body-l',
  medium: 'style-body-m',
  small: 'style-body-m',
  gallery: 'style-body-m',
};

export function ContentBlock(
  props: IContentBlock & HTMLAttributes<HTMLElement>,
) {
  const {
    className,
    layout,
    heading,
    subHeading,
    content,
    richText,
    button,
    type,
    size,
    widthLimit = true,
    speedMultiplier = 1,
    ...rest
  } = props;

  const speed = 25 * speedMultiplier;
  const delay = 0.35;
  const headingLength = heading?.length ?? 0;
  const fadeDelay = Math.round((delay + headingLength / speed) * 1000);

  const hasTitle = !!heading;
  const hasSubHeading = !!subHeading;
  const hasContent = !richText && content;
  const hasRichText = !!richText;
  const hasButtons = button && button.length > 0;

  return (
    <section
      className={cn('z-10 relative w-full', {
        'md:gridwidth4-[3.5] lg:gridwidth12-[7.5]': !!widthLimit,
      })}
    >
      <div
        {...rest}
        className={cn(
          className,
          'flex flex-wrap flex-col',
          layoutClasses[layout],
        )}
      >
        {hasTitle && (
          <h2
            aria-label={heading}
            className={cn(typeClasses[type], sizeClasses[size], 'moveText', {
              'mb-2 md:mb-2':
                hasSubHeading || hasContent || hasRichText || hasButtons,
            })}
          >
            <SplitTextMulti
              copy={heading}
              aria-level={
                size === 'large' ? '2' : size === 'medium' ? '3' : '4'
              }
              role={'heading'}
              speed={speed}
              delay={delay}
            />
          </h2>
        )}
        <FadeInSection delay={fadeDelay}>
          {hasSubHeading && (
            <h3
              data-delay={fadeDelay}
              className={cn(typeClasses[type], sizeSubClasses[size], {
                'mb-2 md:mb-4': hasContent || hasRichText || hasButtons,
              })}
            >
              {subHeading}
            </h3>
          )}
          {hasContent && (
            <p
              data-delay={fadeDelay}
              className={cn(
                'space-y-4',
                typeClassesContent[type],
                sizeBodyClasses[size],
                {
                  'mb-4 md:mb-8': hasButtons,
                },
              )}
            >
              {content}
            </p>
          )}
          {hasRichText && (
            <div
              data-delay={fadeDelay}
              className={cn(
                'space-y-4',
                typeClassesContent[type],
                sizeBodyClasses[size],
                {
                  'mb-4 md:mb-8': hasButtons,
                },
              )}
            >
              <TextRichText json={richText?.json} />
            </div>
          )}
          {hasButtons && (
            <div className={cn('flex flex-row gap-4', layoutClasses[layout])}>
              {button &&
                button.map((button, index) => {
                  return (
                    <Suspense key={index} fallback={null}>
                      <CtaProvider
                        inContentBlock={true}
                        buttonType={type}
                        size={size}
                        item={button}
                        key={index}
                      />
                    </Suspense>
                  );
                })}
            </div>
          )}
        </FadeInSection>
      </div>
    </section>
  );
}
